<template>
  <v-dialog :value="value" max-width="640" persistent @input="close">
    <v-card v-if="realign" class="realign">
      <v-card-title class="realign__title">
        {{ $t("realignDialog.title") }}
      </v-card-title>

      <v-card-text>
        <p class="realign__lead">
          {{ $t("realignDialog.lead", { subject: realign.subject || "" }) }}
        </p>

        <div v-if="realign.lessons.length" class="realign__section">
          <div class="realign__section-title">
            {{ $t("realignDialog.changed") }}
          </div>

          <div
            v-for="lesson in realign.lessons"
            :key="`c-${lesson.id}`"
            class="realign__row"
          >
            <div class="realign__date">{{ formatDate(lesson.date) }}</div>
            <div class="realign__subjects">
              <span class="realign__from">{{ subjectOf(lesson.current) }}</span>
              <v-icon x-small class="realign__arrow">mdi-arrow-right</v-icon>
              <span class="realign__to">{{ lesson.next }}</span>
            </div>
          </div>
        </div>

        <div v-if="realign.excess.length" class="realign__section">
          <div class="realign__section-title realign__section-title--danger">
            {{ $t("realignDialog.removed") }}
          </div>

          <div
            v-for="lesson in realign.excess"
            :key="`x-${lesson.id}`"
            class="realign__row realign__row--removed"
          >
            <div class="realign__date">{{ formatDate(lesson.date) }}</div>
            <div class="realign__subjects">
              <span class="realign__from">{{ subjectOf(lesson.current) }}</span>
            </div>
          </div>

          <p class="realign__hint">{{ $t("realignDialog.removedHint") }}</p>
        </div>

        <p class="realign__hint">{{ $t("realignDialog.keepsHint") }}</p>
      </v-card-text>

      <v-card-actions class="realign__actions">
        <v-spacer />
        <v-btn text :disabled="busy" @click="keep">
          {{ $t("realignDialog.keep") }}
        </v-btn>
        <v-btn
          depressed
          dark
          rounded
          color="#a826e1"
          :loading="busy"
          :disabled="busy"
          @click="confirm"
        >
          {{ $t("realignDialog.confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";

// Retorno de 04/11 (card 17trt6jhfrb): logo depois de trocar o assunto de uma
// aula, esta janela mostra as datas das próximas aulas com o assunto que cada
// uma passaria a ter (e as que seriam excluídas). O professor decide ali.
export default {
  name: "SubjectRealignDialog",
  data() {
    return { value: false, realign: null, busy: false };
  },
  methods: {
    // `realign` é o que a API devolve junto com a troca de assunto; sem
    // aulas a mudar nem a excluir, não há o que perguntar.
    open(realign) {
      if (
        !realign?.offered ||
        (!(realign.lessons || []).length && !(realign.excess || []).length)
      ) {
        return false;
      }

      this.realign = {
        ...realign,
        lessons: realign.lessons || [],
        excess: realign.excess || [],
      };
      this.value = true;

      return true;
    },
    close() {
      if (this.busy) return;
      this.value = false;
    },
    keep() {
      this.close();
    },
    async confirm() {
      this.busy = true;

      try {
        const { data } = await partnerApi.realignSubjects(
          this.realign.classId,
          this.realign.eventId
        );
        const changed = Number(data?.changed || 0);
        const removed = Number(data?.removed || 0);

        this.$root.$emit("display-alert", {
          text:
            changed || removed
              ? this.$t("notificationsPage.realignDone", { changed, removed })
              : this.$t("notificationsPage.realignNothing"),
          type: 0,
        });
        this.value = false;
        this.$emit("done");
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("notificationsPage.realignFailed"),
          type: 1,
        });
      } finally {
        this.busy = false;
      }
    },
    // As datas das aulas são horário de parede guardado como UTC: ler os
    // campos em UTC evita o dia deslocado pelo fuso do navegador.
    formatDate(value) {
      const date = new Date(value);

      if (Number.isNaN(date.getTime())) return "";

      return new Intl.DateTimeFormat(this.$i18n?.locale || "pt-BR", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "UTC",
      }).format(date);
    },
    // O título da aula é "Participantes - Assunto": só o assunto interessa.
    subjectOf(title) {
      const text = String(title || "");
      const at = text.lastIndexOf(" - ");

      return at >= 0 ? text.slice(at + 3) : text;
    },
  },
};
</script>

<style scoped>
.realign__title {
  font-size: 1.1rem;
  font-weight: 700;
}

.realign__lead {
  font-size: 0.95rem;
  color: var(--dk-ink-soft, #4b4551);
}

.realign__section {
  margin-top: 16px;
}

.realign__section-title {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--dk-muted, #6b6472);
  margin-bottom: 8px;
}

.realign__section-title--danger {
  color: #b3261e;
}

.realign__row {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 8px 10px;
  border: 1px solid #e4e0e9;
  border-radius: 10px;
  margin-bottom: 6px;
  font-size: 0.88rem;
}

.realign__row--removed {
  border-color: #f2c4c0;
  background: rgba(179, 38, 30, 0.05);
}

.realign__date {
  flex: 0 0 132px;
  font-weight: 700;
  text-transform: capitalize;
}

.realign__subjects {
  flex: 1;
  min-width: 0;
}

.realign__from {
  color: var(--dk-muted, #6b6472);
}

.realign__row--removed .realign__from {
  text-decoration: line-through;
}

.realign__arrow {
  margin: 0 6px;
}

.realign__to {
  font-weight: 700;
}

.realign__hint {
  margin: 10px 0 0;
  font-size: 0.8rem;
  color: var(--dk-muted, #6b6472);
}

.realign__actions {
  padding: 8px 24px 20px;
}

@media (max-width: 600px) {
  .realign__row {
    flex-direction: column;
    gap: 2px;
  }
  .realign__date {
    flex-basis: auto;
  }
}
</style>
